import { apiRequest } from '@/lib/api-client';
import type {
  InvitableRole,
  Invitation,
  InvitationPreview,
  Member,
  Organization,
  OrgRole,
  OrgSettings,
} from './types';

export interface Plan {
  id: string;
  name: string;
  priceUsd: number;
  maxProjects: number | null;
  seats: number | null;
  monthlyAiTokenCap: number | null;
  monthlyAdSpendCap: number | null;
}

export interface Billing {
  plan: Plan;
  plans: Plan[];
  usage: { projects: number; seats: number; aiTokens: number; adSpend: number };
}

const BASE = '/api/v1/organizations';

export const organizationsApi = {
  list: () => apiRequest<Organization[]>(BASE),

  create: (body: { name: string }) => apiRequest<Organization>(BASE, { method: 'POST', body }),

  update: (orgId: string, body: { name: string }) =>
    apiRequest<Organization>(`${BASE}/${orgId}`, { method: 'PATCH', body }),

  updateSettings: (orgId: string, body: Partial<OrgSettings>) =>
    apiRequest<Organization>(`${BASE}/${orgId}/settings`, { method: 'PATCH', body }),

  getBilling: (orgId: string) => apiRequest<Billing>(`${BASE}/${orgId}/billing`),
  setPlan: (orgId: string, plan: string) =>
    apiRequest<Billing>(`${BASE}/${orgId}/billing/plan`, { method: 'PATCH', body: { plan } }),

  remove: (orgId: string) =>
    apiRequest<{ deleted: boolean }>(`${BASE}/${orgId}`, { method: 'DELETE' }),

  leave: (orgId: string) =>
    apiRequest<{ left: boolean }>(`${BASE}/${orgId}/leave`, { method: 'POST' }),

  listMembers: (orgId: string) => apiRequest<Member[]>(`${BASE}/${orgId}/members`),

  updateMemberRole: (orgId: string, userId: string, role: OrgRole) =>
    apiRequest<Member>(`${BASE}/${orgId}/members/${userId}`, { method: 'PATCH', body: { role } }),

  removeMember: (orgId: string, userId: string) =>
    apiRequest<{ removed: boolean }>(`${BASE}/${orgId}/members/${userId}`, { method: 'DELETE' }),

  listInvitations: (orgId: string) => apiRequest<Invitation[]>(`${BASE}/${orgId}/invitations`),

  createInvitation: (orgId: string, body: { email: string; role: InvitableRole }) =>
    apiRequest<Invitation>(`${BASE}/${orgId}/invitations`, { method: 'POST', body }),

  revokeInvitation: (orgId: string, invitationId: string) =>
    apiRequest<{ revoked: boolean }>(`${BASE}/${orgId}/invitations/${invitationId}`, {
      method: 'DELETE',
    }),

  previewInvitation: (token: string) =>
    apiRequest<InvitationPreview>(`/api/v1/invitations/${token}`),

  acceptInvitation: (token: string) =>
    apiRequest<{ organizationId: string; organizationName: string; role: OrgRole }>(
      `/api/v1/invitations/${token}/accept`,
      { method: 'POST' },
    ),
};
